<template>
  <div class="w-full pt-5 pb-5">
    <div v-if="hasAccessModule">
      <!-- Header Section -->
      <div class="flex">
        <back-button label="Back"
          @onClick="$router.push({ name: 'OrganisationlGoal', query: { activeTab: 'Current Goals' } })"
          variant="secondary" class="mr-4" />
        <div class="mt-4" style="border-left: 1px solid #878e99; height: 1.5rem" />
        <div style="width: 150px">
          <h1 class="text-xl text-left font-extrabold ml-4 mt-3">Edit Goal</h1>
        </div>
        <div class="w-7/12 mt-4">
          <Breadcrumb :items="breadcrumbs" />
        </div>
      </div>

      <!-- Loading State -->
      <div style="height: 100%" class="mt-20" v-if="loading">
        <loader size="xxs" :loader-image="false" />
      </div>

      <!-- Main Content -->
      <ValidationObserver v-else ref="formObserver" v-slot="{ invalid }">
        <!-- Goal Details Section -->
        <div class="px-3">
          <card class="p-5 mt-3">
            <p class="text-lg font-bold">Goal Details</p>
            <div class="grid grid-cols-12 gap-7 mt-4">
              <!-- Financial Year -->
              <div class="col-span-4">
                <div class="text-sm text-jet">Financial Year</div>
                <div class="date-flex mt-1 bg-disabled">
                  <datepicker placeholder="--Select Year--" style="width: 100%; outline: none" input-class="date-input"
                    format="yyyy" minimum-view="year" :disabled="true" v-model="payload.year" />
                </div>
              </div>

              <!-- Description -->
              <div class="col-span-4">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <c-text placeholder="--Enter--" variant="w-full" class="mt-1" v-model="payload.description"
                    label="Description" :error="errors[0]" />
                </ValidationProvider>
              </div>

              <!-- Template -->
              <div class="col-span-4">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <c-select label="Template" placeholder="Select Template" variant="w-full" :options="templateOptions"
                    class="mt-1" v-model="payload.templateId" @input="selectedTemplate" :disabled="true"
                    :error="errors[0]" />
                </ValidationProvider>
              </div>
            </div>

            <!-- Cycle Details -->
            <div v-if="checkTemplate" class="w-full p-2 h-auto flex flex-col mt-5 cycle">
              <div class="w-11/12 flex cycle__text">CYCLE DETAILS</div>
              <card class="w-full fcard">
                <div class="w-full flex h-auto">
                  <div class="fcard__icon">
                    <icon :icon-name="doccycle" size="csm" />
                  </div>
                  <div class="w-full flex flex-col h-auto">
                    <div class="fcard__header" style="color: #490166">
                      {{ cycleTitle }}
                    </div>
                    <div class="w-full fcard__text">
                      {{ cycleDescription }}
                    </div>
                  </div>
                </div>
              </card>

              <div class="w-full h-auto flex flex-wrap">
                <card class="w-5/12 flex flex-grow mr-3 fcard">
                  <div class="w-full flex h-auto">
                    <div class="fcard__icon">
                      <icon :icon-name="calendarIcon" size="csm" />
                    </div>
                    <div class="w-full flex flex-col h-auto">
                      <div class="fcard__header" style="color: #e99323">
                        Cycle Period
                      </div>
                      <div class="w-full fcard__text">
                        {{ $DATEFORMAT(new Date(cycleStartPeriod), "MMMM dd yyyy") }} -
                        {{ $DATEFORMAT(new Date(cycleEndPeriod), "MMMM dd yyyy") }}
                      </div>
                    </div>
                  </div>
                </card>

                <card class="w-5/12 flex flex-grow mb-3 fcard" v-for="(cycles, index) in appraisal_cycles" :key="index">
                  <div class="w-full flex h-auto">
                    <div class="fcard__icon">
                      <icon :icon-name="reviewIcon" size="csm" />
                    </div>
                    <div class="w-full flex flex-col h-auto">
                      <div class="fcard__header" style="color: #2176ff">
                        {{ cycles.name }}
                      </div>
                      <div class="w-full fcard__text">
                        {{ $DATEFORMAT(new Date(cycles.appraisal_starts), "MMMM dd yyyy") }} -
                        {{ $DATEFORMAT(new Date(cycles.appraisal_ends), "MMMM dd yyyy") }}
                      </div>
                    </div>
                  </div>
                </card>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="w-full p-2 h-auto flex flex-col mt-5"
              style="border: 1px dashed #878e99; border-radius: 5px">
              <div class="w-11/12 h-6 p-1 flex cyc-text">PERFORMANCE DETAILS</div>
              <div class="w-11/12 h-6 p-1 mt-4 mb-3 inner-text">
                --Select a Performance Template to view Appraisal Cycle details here---
              </div>
            </div>
          </card>
        </div>

        <!-- KPAs Section -->
        <div class="px-3">
          <card class="p-5 mt-4">
            <div class="w-full flex justify-between items-center" style="height: 44px;">
              <h1 class="text-base font-bold">KPAs</h1>

              <div class="w-auto flex items-center">
                <!-- Weight Summary -->
                <div class="p-2 mx-3 bg-ghostWhite h-full flex items-center rounded">
                  <span class="text-sm font-black flex items-center flex-grow">
                    Total Allocated Weight:
                  </span>
                  <span class="text-sm ml-3" :class="allocatedWeight === 100 ? 'text-darkPurple' : 'text-desire'">
                    {{ allocatedWeight }}%
                  </span>
                  <icon :icon-name="iconInfo" size="ksm" />
                </div>

                <!-- Import Buttons -->
                <div class="h-4 mx-2 w-px bg-gray-400" />
                <div class="w-auto flex cursor-pointer gap-1 items-center" @click="importGoalModal = true">
                  <Icon icon-name="import" style="color: white; fill: #FFFFFF" size="xsm" />
                  <span class="text-blueCrayola text-base">Import from Previous Agreement</span>
                </div>

                <div class="h-4 mx-2 w-px bg-gray-400" />
                <div class="w-auto flex cursor-pointer gap-1 items-center" @click="impExpOptSelected()"
                  v-if="payload.templateId && payload.year && payload.description">
                  <Icon icon-name="import" style="color: white; fill: #FFFFFF" size="xsm" />
                  <span class="text-blueCrayola text-base">Import from File</span>
                </div>
              </div>
            </div>

            <div class="border-b border-gray-300 my-4" />

            <!-- KPA List -->
            <div class="flex flex-col w-full my-4">
              <div class="w-full h-auto flex flex-col">
                <accordion class="mt-6" v-for="(kpaTypes, kpaNumber) in templateKPATypes" :key="'kpa-' + kpaNumber"
                  show-divider @open="handleOpen($event, kpaTypes.id)" :is-open="true">

                  <!-- KPA Header -->
                  <template v-slot:title>
                    <ValidationObserver :ref="`kpa-observer-${kpaTypes.id}`" v-slot="{ handleSubmit }">
                      <div class="flex gap-3">
                        <span class="font-bold">{{ kpaTypes.name }}</span>
                        <span class="flex cursor-pointer" @click="handleSubmit(() => createNewKPA(kpaTypes))">
                          <icon icon-name="icon-plus" size="xs" />
                        </span>
                        <span v-if="payload.status !== 'active'" class="flex cursor-pointer text-blueCrayola" @click="toggleEditMode(kpaTypes)">
                          <icon :icon-name="isKpaEditable(kpaTypes) ? 'icon-save' : 'icon-edit'" size="xs" />
                        </span>
                      </div>
                    </ValidationObserver>
                  </template>

                  <!-- KPA Weight Summary -->
                  <template v-slot:block>
                    <div class="p-2 w-auto flex gap-2 items-center bg-ghostWhite rounded-sm mr-2">
                      <span class="font-black text-xs"
                        :class="getKPAWeight(kpaTypes, kpaTypes.id) > getTotalWeight(kpaTypes, kpaTypes.id) ? 'text-desire' : 'text-mediumSeaGreen'">
                        {{ getKPAWeight(kpaTypes, kpaTypes.id) }}
                      </span>
                      <span class="font-black text-xs"> of </span>
                      <span class="font-black text-xs" :class="getKPAWeight(kpaTypes, kpaTypes.id) > getTotalWeight(kpaTypes, kpaTypes.id) ? 'text-desire' : 'text-mediumSeaGreen'">
                        {{ getTotalWeight(kpaTypes, kpaTypes.id) }}%
                      </span>
                    </div>
                  </template>

                  <!-- KPA Content -->
                  <div class="flex flex-col -mt-4 w-full">
                    <!-- Strategic Goal -->
                    <div v-if="!isKpaEditable(kpaTypes)" class="w-full mb-6 p-4 bg-alertRed rounded-lg shadow-sm">
                      <h2 class="text-base font-bold text-gray-800 mb-2">STRATEGIC GOAL</h2>
                      <p class="text-gray-600">{{ kpaTypes.description }}</p>
                    </div>

                    <ValidationProvider v-else tag="div" rules="required" v-slot="{ errors }">
                      <div class="w-full mb-6 p-4 rounded-lg shadow-sm">
                        <h2 class="text-base font-bold text-gray-800 normal-case mb-2">Strategic Goal</h2>
                        <c-textarea label="" placeholder="--Enter--" v-model="kpaTypes.description"
                          :error="errors[0]" />
                      </div>
                    </ValidationProvider>

                    <!-- KPI Items -->
                    <div class="w-full flex flex-col gap-6">
                      <accordion class="px-5 -mt-1 mb-0 bg-gray-50 shadow-sm rounded-lg"
                        v-for="(item, index) in kpaTypes.kpas" :key="'kpi-' + index" :is-open="true"
                        :iconClass="isKpaEditable(kpaTypes) ? 'mt-4' : ''">

                        <template v-slot:block>
                          <div class="w-full flex flex-col lg:flex-row items-start justify-between gap-4">
                            <!-- Left Section (KPI and Objective) -->
                            <div class="flex items-start w-full lg:w-auto flex-1 gap-2">
                              <!-- Delete Button -->
                              <div v-if="!item.isLocked" class="flex items-center"
                                :class="!item.isEdit ? 'h-7 mt-1' : 'h-8 mt-2'">
                                <button @click="deleteKPA(index, kpaTypes)"
                                  class="text-gray-500 hover:text-red-500 transition-colors">
                                  <icon icon-name="icon-trash" size="xs" />
                                </button>
                              </div>

                              <div :class="!item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'"
                                class="w-auto mx-2 flex items-center">
                                <span class="w-px bg-gray-300" :class="!item.isEdit ? 'h-6' : 'h-8'"></span>
                              </div>

                              <!-- KPI Field -->
                              <div v-if="!item.isEdit" class="flex gap-2 whitespace-nowrap mt-2">
                                <span class="font-bold text-flame text-sm">KPI:</span>
                                <span class="font-semibold text-sm" v-tooltip.bottom-middle="item.name
                                  ">{{ item.data?.name || item.name }}</span>
                              </div>

                              <ValidationProvider v-else rules="required" v-slot="{ errors }"
                                class="min-w-[200px] lg:min-w-[250px]">
                                <div class="flex items-center gap-2 w-full">
                                  <span class="font-bold text-flame text-sm whitespace-nowrap">KPI :</span>
                                  <c-text placeholder="--Enter--" variant="w-full" :style="'width: 20rem'"
                                    :disabled="item.data?.isLocked || item?.isLocked" v-model="item.name"
                                    :error="errors[0]" />
                                </div>
                              </ValidationProvider>

                              <div :class="!item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'"
                                class="w-auto mx-2 flex items-center">
                                <span class="w-px bg-gray-300" :class="!item.isEdit ? 'h-6' : 'h-8'"></span>
                              </div>

                              <!-- Objective Field -->
                              <div v-if="!item.isEdit" class="flex gap-2 whitespace-nowrap mt-2">
                                <span class="font-bold text-flame text-sm">Objective:</span>
                                <span class="font-semibold text-sm" v-tooltip.bottom-middle="item.objective
                                  ">{{ item.data?.objective || item.objective }}</span>
                              </div>

                              <ValidationProvider v-else rules="required" v-slot="{ errors }"
                                class="flex-1 min-w-[200px] lg:min-w-[250px]">
                                <div class="flex items-center gap-2 w-full">
                                  <span class="font-bold text-flame uppercase text-sm whitespace-nowrap">Objective
                                    :</span>
                                  <c-text placeholder="--Enter--" variant="w-full" class="w-[300px]"
                                    :style="'width: 20rem'" :disabled="item.data?.isLocked || item?.isLocked"
                                    v-model="item.objective" :error="errors[0]" />
                                </div>
                              </ValidationProvider>
                            </div>

                            <!-- Right Section (Weight) -->
                            <div class="w-full lg:w-auto lg:ml-8 mt-2 mr-3 lg:mt-0">
                              <div v-if="!item.isEdit" class="p-2 w-auto flex gap-2 items-center rounded-sm mr-2 mt-1">
                                <span class="font-black text-xs">{{ item.data?.weight || item.weight }}%</span>
                              </div>

                              <ValidationProvider v-else rules="required" v-slot="{ errors }"
                                class="flex items-center gap-2">
                                <span class="font-bold text-flame uppercase text-sm whitespace-nowrap">Weight :</span>
                                <c-text placeholder="--Enter--" variant="w-full" class="w-14" type="number"
                                  v-model="item.weight" :disabled="item.isLocked" :error="errors[0]"
                                  @input="validateWeight(item.weight)" />
                              </ValidationProvider>
                            </div>
                          </div>
                        </template>

                        <div class="w-full h-[1px] mb-6 -mt-3 border border-2" />

                        <!-- Criteria Section -->
                        <div class="w-full flex flex-col gap-1">
                          <span class="text-sm uppercase font-bold">Criteria</span>
                        </div>
                        <div class="w-full grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                          <ValidationProvider v-for="(n, rating) in (item.data?.kpis || item.kpis)" :key="rating"
                            rules="required" v-slot="{ errors }">
                            <c-text variant="w-full" :placeholder="`Criteria ${lowest_rating + rating}`"
                              v-tooltip.bottom-middle="item.kpis[rating].criteria
                                " :disabled="!item.isEdit" v-model="item.kpis[rating].criteria" :error="errors[0]" />
                          </ValidationProvider>
                        </div>
                      </accordion>
                    </div>
                  </div>
                </accordion>
              </div>
            </div>

            <!-- Share Options -->
            <ValidationProvider rules="required" v-slot="{ errors }">
              <div class="w-full flex p-1 mt-2" v-if="payload.templateId">
                <radio-button :options="optionBox" col-span="col-span-4"
                  class="inline-block text-darkPurple mr-4 gap-x-12 radio" @change="getSelected($event)"
                  v-model="payload.shareWith" :error="errors[0]" />
              </div>
            </ValidationProvider>
          </card>
        </div>

        <!-- Action Buttons -->
        <div class="pl-3 flex mt-5">
          <div class="flex flex-grow" v-if="payload.status === 'draft'">
            <Button :background="appearance.buttonColor || defaultBTNColor" width="8rem"
              :disabled="isSaveDisabled || invalid" @click="submit" style="color: #FFFFFF">
              Save
            </Button>
            <Button background-color="none" class="ml-4" style="color: #321c3b"
              @click="$router.push({ name: 'OrganisationlGoal', query: { activeTab: 'Current Goals' } })">
              Cancel
            </Button>
          </div>
          <div class="flex justify-end mr-5">
            <Button class="text-dynamicBackBtn border border-solid border-dynamicBackBtn text-center" width="8rem"
              @click="submit('draft')" v-if="payload.status === 'draft'">
              Save Draft
            </Button>
          </div>
        </div>
      </ValidationObserver>
      <RightSideBar v-if="weightAdjust" @close="weightAdjust = false" submit="Save" @submit="saveWeight"
        button-class="bg-dynamicBackBtn text-white">
        <template v-slot:title>
          <div class="w-full flex">
            <p class="text-darkPurple mt-3">Edit Weight</p>
          </div>
        </template>
        <template v-slot:subtitle>
          <div class="flex flex-col w-full mb-3">
            <p class="my-2 text-base">Edit KPA Weights</p>
          </div>
        </template>

        <div class="w-full flex flex-col">
          <div class="w-full flex py-4 mb-1">
            <span class="text-base text-darkPurple flex flex-grow mt-2 font-semibold">
              Total Weight
            </span>
            <c-text placeholder="" variant="w-full" class="-mt-2 w-20" :disabled="true" v-model="totalWeight" />
          </div>

          <div class="w-full flex flex-col">
            <div class="w-full flex py-4 mb-1" v-for="(kpaTypes, kpaNumber) in kpaWeights" :key="kpaNumber">
              <span class="text-base text-darkPurple flex flex-grow mt-2">
                {{ kpaTypes.name }}
              </span>
              <div class="flex flex-col w-20 mr-2 -ml-1">
                <span class="flex flex-col relative">
                  <span class="w-full" style="
                          height: 40px;
                          padding: 10px 15px;
                          width: 100%;
                          border-radius: 5px;
                          color: #878e99;
                          background-color: #eeeeee;
                          line-height: 120%;
                          letter-spacing: 0.015em;
                          text-align: center;
                          font-weight: 600;
                        " :class="kpaTypes.weight > kpaTypes.totalKpiWeight
                          ? `text_desire`
                          : ``
                          ">
                    {{ kpaTypes.weight }}
                  </span>
                </span>
              </div>
              <span class="text-base text-darkPurple flex mt-2 mr-2 items-center">
                of
              </span>
              <c-text placeholder="" variant="w-full" class="-mt-2 w-20" type="number" v-model="kpaTypes.totalKpiWeight"
                @input="computeKPAWeights" />
            </div>
          </div>
        </div>
      </RightSideBar>

      <RightSideBar v-if="addOthersModal" @close="addOthersModal = false" submit="Proceed" @submit="employeeSubmit"
        :button-class="`bg-dynamicBackBtn text-white`">
        <template v-slot:title>
          <div class="w-full flex">
            <p class="text-darkPurple mt-3">Add Others</p>
          </div>
        </template>
        <template v-slot:subtitle>
          <div class="flex flex-col w-full mb-3">
            <p class="my-2 text-base">Search existing employees to add.</p>
          </div>
        </template>

        <search-input class="my-4" @input="startSearch" placeholder="Search" />

        <div style="height: 100%" v-if="loadingBar">
          <loader size="xxs" :loader-image="false" />
        </div>
        <div v-else>
          <div v-for="(item, index) in employees" :key="index"
            class="flex flex-col py-4 userContainer border-b border-dashed">
            <div class="flex items-center">
              <div>
                <img class="mr-3" :src="item.photo" v-if="item.photo" alt="user photo"
                  style="height: 35px; width: 35px; border-radius: 5px" />
                <div style="height: 35px; width: 35px; border-radius: 5px"
                  class="text-blueCrayola mr-3 flex justify-center border text-center font-semibold pt-1" v-else>
                  {{ $getInitials(`${item.fname} ${item.lname}`) }}
                </div>
              </div>
              <div class="flex flex-col flex-grow">
                <p>
                  {{ `${item.fname} ${item.lname}` }}
                </p>
                <div class="text-xs flex font-semibold w- text-romanSilver uppercase">
                  <p class="opacity-50 mr-2" style="font-size: 10px">
                    {{
                      `${item.orgFunction ? item.orgFunction.name : "---"}`
                    }}
                  </p>
                  <p class="opacity-50 ml-1" style="font-size: 10px">
                    <span class="dot dot-circle secondary" style="width: 6px; height: 6px" />
                  </p>
                  <p class="opacity-50 ml-2" style="font-size: 10px">
                    {{
                      `${item.userDesignation
                        ? item.userDesignation.name
                        : "---"
                      }`
                    }}
                  </p>
                </div>
              </div>
              <checkbox checkbox-size="height:16px; width:16px; margin:4px 7px 8px 8px;" label-style="color:#333333; "
                class="mr-6" :value="[item]" v-model="item.selected" />
            </div>
          </div>
          <p class="text-flame ml-4 mt-10 cursor-pointer" @click="getAllEmployees(`&page=${pageNo}`)">
            {{ showMore ? "Show More" : "Show Less" }}
          </p>
        </div>
      </RightSideBar>
    </div>

    <div v-else>
      <ErrorComponent />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required } from 'vee-validate/dist/rules';
import Datepicker from "vuejs-datepicker";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import SearchInput from "@scelloo/cloudenly-ui/src/components/search-input";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import Card from "@/components/Card";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import RightSideBar from "@/components/RightSideBar";
import Checkbox from "@/components/Checkbox";
import Accordion from "@/components/Accordion";
import RadioButton from "@/components/RadioButton";
import paramsMixin from "@/utilities/paramsMixin";
import timeFormatMixin from "@/utilities/timeFormatMixin";
import ErrorComponent from "@/modules/Admin/error403";

extend('required', {
  ...required,
  message: 'This field is required'
});

export default {
  mixins: [paramsMixin, timeFormatMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    BackButton,
    ErrorComponent,
    CTextarea,
    Breadcrumb,
    Icon,
    CText,
    RadioButton,
    Card,
    Button,
    Checkbox,
    RightSideBar,
    SearchInput,
    CSelect,
    Accordion,
    Datepicker,
  },
  data() {
    return {
      breadcrumbs: [
        { text: "Performance", href: "performance", id: "Performance" },
        { text: "Organisational Goals and Objectives", href: "Goals", id: "Goals" },
        { text: "Edit Goal", id: "Edit goal" },
      ],
      optionBox: [
        { name: "Share with CEO", value: "ceo" },
        { name: "Share with CEO & Direct Reports", value: "ceo_and_direct_reports" },
        { name: "Share with CEO & Others", value: "ceo_and_others" },
      ],
      illustration: "illustration",
      weightAdjust: false,
      allocatedWeight: 0,
      templateKPATypes: [],
      loadingBar: true,
      hasAccessModule: false,
      loading: true,
      showMore: false,
      lowest_rating: "",
      pageNo: "",
      employees: [],
      kpaWeights: [],
      totalWeight: "",
      doccycle: "doc-cycle",
      calendarIcon: "calendar",
      reviewIcon: "reviewicon",
      iconInfo: "icon-info",
      iconPlus: "icon-plus",
      addOthersModal: false,
      Ratings: [],
      templateOptions: [],
      selectedTemplate: "",
      cycleStartPeriod: "",
      cycleEndPeriod: "",
      cycleDescription: "",
      cycleTitle: "",
      appraisal_cycles: [],
      kpaData: [],
      payload: {
        orgId: "",
        templateId: "",
        templateLinkId: "",
        description: "",
        year: "",
        status: "draft",
        kpa_types: [],
        shareWith: null,
        sharedEmployees: [],
      },
    };
  },
  computed: {
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),

    isSaveDisabled() {
      return (
        !this.payload.year ||
        !this.payload.description ||
        !this.payload.templateId ||
        !this.payload.shareWith ||
        this.allocatedWeight !== 100 ||
        this.templateKPATypes.some(kpaType =>
          kpaType.kpas.some(kpa =>
            !kpa.name ||
            !kpa.objective ||
            !kpa.weight ||
            kpa.kpis.some(kpi => !kpi.criteria)
          )
        )
      )
    },

    checkTemplate() {
      return !!this.payload.templateId;
    }
  },
  watch: {
    selectedTemplate(value) {
      if (value) {
        this.getOneTemplate(value);
      }
    },

    templateKPATypes: {
      deep: true,
      handler() {
        this.calculateAllocatedWeight();
      }
    }
  },

  methods: {
    getSelected(value) {
      if (value === "ceo_and_others") {
        this.addOthersModal = true;
      }
    },

    calculateAllocatedWeight() {
      this.allocatedWeight = this.sumKpaWeights(this.templateKPATypes);
    },

    sumKpaWeights(data) {
      return data.reduce((totalWeight, item) => {
        const kpaWeightSum = item.kpas.reduce((sum, kpa) => {
          const weight = Number(kpa.weight) || 0;
          return sum + weight;
        }, 0);
        return totalWeight + kpaWeightSum;
      }, 0);
    },

    validateWeight(weight) {
      if (weight < 0) return "Value must be ≥ 0";
      if (weight > 100) return "Value must be ≤ 100";
      return true;
    },

    getKPAWeight(kpaTypes, kpaTypesId) {
      if (kpaTypes.id === kpaTypesId) {
        return kpaTypes.kpas.reduce((sum, kpi) => sum + Number(kpi.weight), 0) || 0;
      }
      return 0;
    },

    getTotalWeight(kpaTypes, kpaTypesId) {
      const kpa = this.kpaWeights.find(kpa => kpa.id === kpaTypesId);
      return kpa ? kpa.totalKpiWeight : 0;
    },

    isKpaEditable(kpaTypes) {
      return kpaTypes.kpas.some(kpa => kpa.isEdit);
    },

    toggleEditMode(kpaTypes) {
      if (!this.isKpaEditable(kpaTypes)) {
        kpaTypes.kpas.forEach(kpa => {
          this.$set(kpa, 'isEdit', true);
        });
      } else {
        const allKPAsValid = kpaTypes.kpas.every(kpa => {
          const hasRequiredFields = kpa.name && kpa.objective && kpa.weight;

          const allKPIsValid = !kpa.kpis || kpa.kpis.every(kpi => kpi.criteria);

          return hasRequiredFields && allKPIsValid;
        });

        if (allKPAsValid) {
          kpaTypes.kpas.forEach(kpa => {
            this.$set(kpa, 'isEdit', false);
          });
          this.saveKpa(kpaTypes); // Uncomment when ready to save
        } else {
          this.$toasted.error('Please fill all required fields before saving', {
            duration: 5000
          });
        }
      }
    },


    saveKpa(kpaTypes) {
      // Create a deep clone of templateKPATypes to avoid modifying the original
      const clonedKPATypes = JSON.parse(JSON.stringify(this.templateKPATypes));

      // Process all KPA types
      this.kpaData = clonedKPATypes.map(kpaType => {
        // For the KPA type we're editing
        if (kpaType.id === kpaTypes.id) {
          const newKPAs = kpaType.kpas.map(({ state, isEdit, target_date, ...rest }) => ({
            ...rest,
          }));

          const validation = newKPAs.every(item =>
            item.name && item.objective && item.weight
          );

          return {
            kpa_types_id: kpaType.id,
            description: kpaType.description,
            kpas: validation ? newKPAs : []
          };
        }
        // For all other KPA types - include them but without isEdit
        else {
          const otherKPAs = kpaType.kpas.map(({ state, isEdit, target_date, ...rest }) => ({
            ...rest,
          }));

          return {
            kpa_types_id: kpaType.id,
            description: kpaType.description,
            kpas: otherKPAs
          };
        }
      });

      // Prepare payload - always set status to goal status
      this.payload = Object.assign({}, this.payload, {
        kpa_types: this.kpaData,
        orgId: this.$orgId,
        sharedEmployees: this.payload.shareWith === "ceo_and_others"
          ? this.employees
            .filter(employee => employee.selected)
            .map(employee => employee.userid)
          : [],
        status: this.payload.status,
        year: this.payload.year ? this.payload.year.getFullYear() : ""
      });

      // Clean up payload
      if (!this.payload.shareWith) {
        delete this.payload.shareWith;
      }

      this.payload.id = this.$route.params.id;

      if (!this.payload.year) {
        this.periodError = true
        return
      }

      this.$_editShortTermGoal(this.payload)
        .then(response => {
          this.showSuccessToast(response.data.message);
          this.getOneGoal();
        })
        .catch(error => {
          this.showErrorToast(error.response.data.message);
        });
    },

    createNewKPA(kpa) {
      const lastKPA = kpa.kpas[kpa.kpas.length - 1];
      const hasEmptyFields =
        !lastKPA.name ||
        !lastKPA.objective ||
        !lastKPA.weight ||
        lastKPA.kpis.some(kpi => !kpi.criteria);

      if (hasEmptyFields) {
        this.$toasted.error('Please complete all fields in the current KPA before adding a new one', {
          duration: 7000
        });
        return;
      }

      if (this.allocatedWeight >= 100) {
        this.$toasted.error(
          "Total allocated weight cannot exceed 100%. Reduce existing weights to add new KPIs.",
          { duration: 7000 }
        );
        return;
      }

      kpa.kpas.push({
        state: true,
        isEdit: true,
        isLocked: false,
        name: null,
        objective: null,
        kpis: this.getCriteriaData(),
        target_date: null,
        weight: "",
      });
    },

    deleteKPA(index, kpaTypes) {
      if (kpaTypes.kpas.length > 1) {
        kpaTypes.kpas.splice(index, 1);
        this.calculateAllocatedWeight();
      } else {
        this.$toasted.error(
          "Cannot delete ALL KPIs of a KPA Type. Edit KPI values instead.",
          { duration: 5000 }
        );
      }
    },

    getCriteriaData() {
      const sortedRatings = [...this.Ratings].sort((a, b) => a.rating - b.rating);
      this.Ratings = sortedRatings;
      this.lowest_rating = Math.min(...this.Ratings.map(item => item.rating));

      return this.Ratings.map(rating => ({
        performanceRatingId: rating.id,
        criteria: "",
      }));
    },


    showSuccessToast(message) {
      this.$toasted.success(message, { duration: 5000 })
    },

    showErrorToast(message) {
      this.$toasted.error(message, { duration: 5000 })
    },

    submit(status) {
      if (this.isSaveDisabled && !status) {
        this.$toasted.error('Please complete all required fields and ensure total weight is exactly 100%', {
          duration: 5000
        });
        return;
      }

      this.kpaData = [];
      this.templateKPATypes.forEach((kpaType) => {
        const newKPAs = kpaType.kpas.map(({ state, isEdit, target_date, ...rest }) => ({
          ...rest,
        }));

        this.kpaData.push({
          kpa_types_id: kpaType.id,
          description: kpaType.description,
          kpas: newKPAs,
        });
      });

      this.payload.kpa_types = this.kpaData;
      this.payload.orgId = this.$orgId;
      this.payload.year = this.payload.year.getFullYear();
      this.payload.id = this.$route.params.id;

      if (!status) {
        this.payload.status = "active";
      }

      if (this.payload.shareWith === "ceo_and_others") {
        this.payload.sharedEmployees = this.employees
          .filter(employee => employee.selected)
          .map(employee => employee.userid);
      } else {
        this.payload.sharedEmployees = [];
      }

      this.$_editShortTermGoal(this.payload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.$router.push({
            name: "OrganisationlGoal",
            query: { activeTab: "Current Goals" },
          });
        })
        .catch((error) => {
          this.$toasted.error(error.response.data.message, { duration: 5000 });
          this.payload.year = new Date(this.payload.year, 0);
        });
    },

    getOneTemplate(templateId) {
      this.$_getOneLinkTemplate(templateId).then((response) => {
        this.cycleDescription =
          response.data.PerformanceTemplate.agreement_description;

        this.cycleTitle = response.data.PerformanceTemplate.agreement_title;
        this.payload.templateId = templateId;

        this.templateKPATypes = response.data.PerformanceTemplate.kpa_types;

        this.templateKPATypes.forEach((kpaType) => {
          const kpaData = kpaType;

          const newKPAs = [];

          if (kpaType.kpas.length > 0) {
            kpaType.kpas.map((kpasData) => {
              newKPAs.push({
                state: true,
                isEdit: false,
                isLocked: kpasData.isLocked,
                name: kpasData.name,
                objective: kpasData.objective,
                kpis: kpasData.kpi,
                target_date: kpasData.target_date,
                weight: kpasData.weight,
              });
              return {};
            });

            kpaData.kpas = newKPAs;
          } else {
            kpaType.kpas.push({
              state: true,
              isEdit: true,
              isLocked: false,
              name: null,
              objective: null,
              kpis: this.getCriteriaData(),
              target_date: null,
              weight: "",
            });
          }

          return {};
        });

        this.loading = false;
      });
    },

    startSearch(value) {
      this.employees = [];
      if (value !== "") {
        const searchQuery = `&search=${value}&page=1`;
        this.loadingBar = true;
        this.employeeSearch = true;
        this.getEmployeesSearch(searchQuery);
      } else {
        this.employeeSearch = false;
        this.getAllEmployees("");
      }
    },

    employeeSubmit() {
      const employeeSelected = this.employees.some(
        (employee) => employee.selected === true
      );

      if (employeeSelected) {
        this.addOthersModal = false;
        this.$toasted.success("Employees Selected", { duration: 5000 });
      } else {
        this.$toasted.error("Select one or more employees to proceed", {
          duration: 5000,
        });
      }
      return {};
    },


    handleOpen(state) {
      if (state) {
        if (this.allocatedWeight > 100) {
          this.disableBtn = false;
        } else {
          this.disableBtn = true;
        }
      } else {
        this.disableBtn = true;
      }
    },


    addKPATable(kpaId, valid) {
      if (!valid) {
        if (this.allocatedWeight < 100) {
          this.templateKPATypes.forEach((kpaType) => {
            if (kpaType.id === kpaId) {
              kpaType.kpas.push({
                state: true,
                isEdit: true,
                isLocked: false,
                name: null,
                objective: null,
                kpis: this.getCriteriaData(),
                target_date: null,
                weight: "",
              });
            }
            return {};
          });
        } else {
          this.$toasted.error(
            "Total allocated weight for all KPA's cannot be greater than 100%. To add new KPI, reduce any existing KPA or KPI weight(s)",
            { duration: 7000 }
          );
        }
      }
    },

    getTemplates() {
      let templatesArr = [];

      this.$_getPerfomanceTemplate("").then((response) => {
        templatesArr = response.data.PerformanceTemplate;
        templatesArr.filter((template) => {
          if (template.template_type !== "draft") {
            this.templateOptions.push({
              id: template.id,
              name: template.title,
            });
          }
          return {};
        });

        this.loading = false;
      });
    },

    getEmployeesSearch(searchQuery) {
      this.employees = [];
      this.$_getAllEmployeesWithQueries(searchQuery).then((response) => {
        this.loadingBar = false;
        response.data.data.employees.map((employee) => {
          if (employee.userReportingTo) {
            this.employees.push({
              id: employee.id,
              userid: employee.userId,
              fname: employee.fname,
              lname: employee.lname,
              orgFunction: employee.orgFunction,
              userDesignation: employee.userDesignation,
              photo: employee.photo,
              selected: false,
            });
          }
          return {};
        });
      });
    },

    getAllEmployees(query) {
      this.loadingBar = true;
      this.$_getAllEmployeesWithQueries(query).then((response) => {
        this.loadingBar = false;
        response.data.data.employees.map((employee) => {
          if (employee.userReportingTo) {
            this.employees.push({
              id: employee.id,
              userid: employee.userId,
              fname: employee.fname,
              lname: employee.lname,
              orgFunction: employee.orgFunction,
              userDesignation: employee.userDesignation,
              photo: employee.photo,
              selected: false,
            });
          }
          return {};
        });

        if (response.data.data.meta.nextPage) {
          this.pageNo = response.data.data.meta.nextPage;
          this.showMore = true;
        } else {
          this.pageNo = response.data.data.meta.prevPage
            ? response.data.data.meta.prevPage
            : 1;
          this.showMore = false;
        }
      });
    },

    getOneGoal() {
      this.$_getOneShortTermGoal(this.$route.params.id).then((response) => {
        const goalData = response.data.goal;

        this.payload.description = goalData.description;
        this.payload.templateId = goalData.templateLinkId;
        this.payload.status = goalData.status;
        this.payload.templateLinkId = goalData.templateLinkId;
        this.payload.year = new Date(goalData.year, 0);
        this.payload.shareWith = goalData.shareWith;
        this.$_getOneLinkTemplate(goalData.templateLinkId).then((responseData) => {
          this.cycleDescription =
            responseData.data.data.description;

          this.cycleTitle =
            responseData.data.data.title;

          this.checkTemplate = true;
          this.$_getKPAWeights(goalData.templateId).then((result) => {
            const kpaData = result.data.data;

            this.kpaWeights = kpaData;

            let sum = 0;

            this.kpaWeights.forEach((kpa) => {
              sum += kpa.totalKpiWeight;
            });

            this.totalWeight = sum;
          });
        });

        this.templateKPATypes = goalData.goal_performance_templatesLink.kpa_types;
        this.templateKPATypes.forEach((kpaType) => {
          const kpaData = kpaType;

          const newKPAs = [];

          if (kpaType.kpas.length > 0) {
            kpaType.kpas.map((kpasData) => {
              newKPAs.push({
                state: true,
                isEdit: false,
                isLocked: kpasData.isLocked,
                name: kpasData.name,
                objective: kpasData.objective,
                kpis: kpasData.kpi,
                target_date: kpasData.target_date,
                weight: kpasData.weight,
              });
              return {};
            });

            kpaData.kpas = newKPAs;
          } else {
            kpaType.kpas.push({
              state: true,
              isEdit: false,
              isLocked: false,
              name: null,
              objective: null,
              kpis: this.getCriteriaData(),
              target_date: null,
              weight: "",
            });
          }
        });

        this.shareWithCeo = goalData.shareWithCeo;
        this.watchOthers = goalData.shareWithOthers;
        this.payload.sharedEmployees = goalData.sharedEmployees;

        if (goalData.sharedEmployees?.length > 0) {
          this.timer = setTimeout(() => {
            goalData.sharedEmployees.map((data) => {
              this.employees.filter((employee) => {
                const employeeData = employee;

                if (employee.userid === data) {
                  employeeData.selected = true;
                }
                return {};
              });
              return {};
            });
          }, 2000);
        }

        if (this.watchOthers) {
          this.edit = true;
        }

        return {};
      });
    },

    getAppraisalCycles() {
      this.$_getAppraisalCycle(this.agreementQuery()).then((response) => {
        const appraisal = response.data.AppraisalCycle;

        appraisal.map((appraisalCycles) => {
          const startDate = this.setDates(appraisalCycles.cycle_start_date);

          const endDate = this.setDates(appraisalCycles.cycle_end_date);

          this.cycleStartPeriod = startDate.toISOString();

          this.cycleEndPeriod = endDate.toISOString();

          appraisalCycles.cycles.forEach((cycle) => {
            const startCycleDate = this.setDates(cycle.appraisal_starts);

            const endCycleDate = this.setDates(cycle.appraisal_ends);

            this.appraisal_cycles.push({
              name: cycle.name,
              appraisal_starts: startCycleDate.toISOString(),
              appraisal_ends: endCycleDate.toISOString(),
            });
          });

          return {};
        });
      });
    },

    watchTotalWeight() {
      let sum = 0;
      this.templateKPATypes.forEach((kpaType) => {
        kpaType.kpas.map((kpi) => {
          sum += Number(kpi.weight);
          return {};
        });
        return {};
      });

      if (sum !== 100) {
        this.maxWeight = false;
        this.disableBtn = true;
      } else {
        this.maxWeight = true;
        this.disableBtn = false;
      }

      this.allocatedWeight = sum;
    },
  },

  async mounted() {
    try {
      await this.$handlePrivilege(
        "organisationalGoals",
        "viewCurrentOrganisationalGoals"
      );
      this.hasAccessModule = true;
      this.$_getPerfomanceRating().then((response) => {
        this.Ratings = response.data.PerformanceRatings;
        this.getTemplates();
        this.getAllEmployees("");
        this.getOneGoal();
        this.getAppraisalCycles();
        setInterval(() => {
          this.watchTotalWeight();
        }, 1000);
      });
    } catch (error) {
      this.hasAccessModule = false;
    }
  },
};
</script>

<style>
.cloudenly-input {
  padding-left: 10px !important;
  padding-right: 10px !important;
  height: 40px !important;
}

.kpas-err small {
  margin-bottom: -1.4rem;
}

#text-input--error {
  border: 1px solid rgba(238, 43, 9, 0.952);
  width: 100%;
  border-radius: 5px;
  color: #321c3b;
  line-height: 120%;
  letter-spacing: 0.015em;
  outline: 0;
}

.text_desire {
  color: rgba(234, 60, 83, 1) !important;
}

.bg-disabled {
  background-color: #eeeeee;
  border-color: transparent;
}

.cyc-text {
  font-size: 12px;
  font-weight: 900;
  line-height: 1.3rem;
  color: #878e99;
}

.inner-text {
  font-size: 12px;
  line-height: 1.9rem;
  font-weight: 600;
  text-align: center;
  color: #878e99;
  text-transform: uppercase;
}

.label label {
  display: block !important;
  font-size: 10px;
  margin: 0;
}


.head {
  width: max-content;
}

.cycle {
  border: 1px dashed #878e99;
  border-radius: 5px;
  padding: 12px;
}

.cycle__text {
  font-size: 12px;
  font-weight: 900;
  line-height: 1.3rem;
  color: #878e99;
  letter-spacing: 0.035em;
  height: 17px;
}

.fcard {
  padding: 12px;
  height: 58px;
  margin-top: 18px;
  line-height: 16.8px;
  text-transform: uppercase;
}

.fcard__icon {
  margin-right: 7.5px;
}

.csm {
  width: 12px;
  height: 12px;
}

.fcard__header {
  font-weight: 900;
  font-size: 12px;
  margin-top: -2px;
  letter-spacing: 0.035em;
}

.fcard__text {
  font-weight: 600;
  font-size: 14px;
  color: #333333;
  margin-right: 7.5px;
  letter-spacing: 0.015em;
}

.allocate {
  width: 320px;
  border-radius: 5px;
  padding: 8px;
  background-color: #f7f7ff;
  font-weight: 900;
  font-size: 12px;
  letter-spacing: 0.035em;
}

.allocate__text {
  line-height: 1.4rem;
  text-transform: uppercase;
  color: #321c3b;
}

.allocate__value {
  line-height: 17px;
  color: #ea3c53;
  margin: 3px 10px;
}

.line {
  width: 24px;
  height: 0px;
  transform: rotate(90deg);
  margin-left: 6px;
  margin-right: 6px;
}

.line--dashed {
  border: 0.5px dashed #878e99;
}

.line--straight {
  border: 1px solid #878e99;
}

.kpaplus {
  width: 180px;
  border-radius: 5px;
  background-color: #ffffff;
  padding: 10px;
}

.kpaplus__text {
  font-weight: 900;
  font-size: 13px;
  line-height: 17px;
  letter-spacing: 0.035em;
  margin: 3px 10px;
  color: #f15a29;
}

.ksm {
  margin-left: 11px;
  color: #ea3c53;
  width: 14px;
  height: 14px;
  margin-top: 4px;
}

.kpalist__text {
  font-size: 18px;
  font-weight: bold;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #321c3b;
  width: 170px;
  height: 22px;
}

.kpalisticon {
  width: 18px;
  height: 18px;
}

.button-layout {
  width: 270px;
  height: 44px;
  margin-top: 29px;
}

.btn-disabled {
  background-color: #eeeeee;
  font-size: 16px;
  cursor: disabled;
}

details>summary {
  display: inline;
  list-style: none;
}
</style>
